import { FolderUp, Loader2, Plus, Upload } from 'lucide-react';
import { useMemo, useState, type ChangeEvent, type RefObject } from 'react';
import { LiksDropdown } from '../../../components/liks/LiksDropdown/LiksDropdown';
import {
  LiksMenu,
  type LiksMenuItem,
} from '../../../components/liks/LiksMenu/LiksMenu';
import { LiksRefreshButton } from '../../../components/liks/LiksRefreshButton/LiksRefreshButton';
import { LiksSearch } from '../../../components/liks/LiksSearch/LiksSearch';

type KnowledgeDocumentsToolbarProps = {
  canManageDocuments: boolean;
  documentSearchInput: string;
  sourceFilterValue: string;
  statusFilterValue: string;
  isUploading: boolean;
  uploadProgress: { current: number; total: number } | null;
  isUploadDragOver: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>;
  folderInputRef: RefObject<HTMLInputElement | null>;
  translate: (key: string, params?: Record<string, string>) => string;
  onDocumentSearchChange: (value: string) => void;
  onSourceFilterChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  onFolderUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  openFilePicker: () => void;
  openFolderPicker: () => void;
  onManualRefresh: () => void;
};

export function KnowledgeDocumentsToolbar({
  canManageDocuments,
  documentSearchInput,
  sourceFilterValue,
  statusFilterValue,
  isUploading,
  uploadProgress,
  isUploadDragOver,
  fileInputRef,
  folderInputRef,
  translate,
  onDocumentSearchChange,
  onSourceFilterChange,
  onStatusFilterChange,
  onUpload,
  onFolderUpload,
  openFilePicker,
  openFolderPicker,
  onManualRefresh,
}: KnowledgeDocumentsToolbarProps) {
  const [isUploadMenuOpen, setIsUploadMenuOpen] = useState(false);

  const uploadMenuItems = useMemo<LiksMenuItem[]>(
    () => [
      {
        id: 'documents',
        type: 'button',
        label: translate('knowledge.uploadDocument'),
        icon: (
          <Upload
            className="knowledge-database__upload-menu-item-icon"
            aria-hidden
          />
        ),
        onClick: openFilePicker,
      },
      {
        id: 'folder',
        type: 'button',
        label: translate('knowledge.uploadFolder'),
        icon: (
          <FolderUp
            className="knowledge-database__upload-menu-item-icon"
            aria-hidden
          />
        ),
        onClick: openFolderPicker,
      },
    ],
    [openFilePicker, openFolderPicker, translate],
  );

  return (
    <>
      {canManageDocuments && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={onUpload}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            hidden
            {...({ webkitdirectory: '', directory: '' } as Record<
              string,
              string
            >)}
            onChange={onFolderUpload}
          />
        </>
      )}
      <div className="knowledge-database__toolbar knowledge-database__toolbar--documents">
        <LiksSearch
          className="knowledge-database__document-search"
          variant="on-light"
          value={documentSearchInput}
          onChange={onDocumentSearchChange}
          placeholder={translate('knowledge.searchDocumentsPlaceholder')}
          clearLabel={translate('common.search.clear')}
          aria-label={translate('knowledge.searchDocumentsLabel')}
        />
        <LiksDropdown
          className="knowledge-database__filter-dropdown"
          variant="on-light"
          value={sourceFilterValue}
          onChange={onSourceFilterChange}
          options={[
            {
              value: 'all',
              label: translate('knowledge.filter.sourceAll'),
            },
            {
              value: 'UPLOAD',
              label: translate('knowledge.filter.sourceUpload'),
            },
            {
              value: 'CRAWL',
              label: translate('knowledge.filter.sourceCrawl'),
            },
            {
              value: 'ARCHIVE_EXTRACT',
              label: translate('knowledge.filter.sourceArchive'),
            },
          ]}
          aria-label={translate('knowledge.filter.sourceLabel')}
        />
        <LiksDropdown
          className="knowledge-database__filter-dropdown"
          variant="on-light"
          value={statusFilterValue}
          onChange={onStatusFilterChange}
          options={[
            {
              value: 'all',
              label: translate('knowledge.filter.statusAll'),
            },
            {
              value: 'PENDING',
              label: translate('knowledge.documentStatus.PENDING'),
            },
            {
              value: 'SCANNING',
              label: translate('knowledge.documentStatus.SCANNING'),
            },
            {
              value: 'UPLOADING',
              label: translate('knowledge.documentStatus.UPLOADING'),
            },
            {
              value: 'EXTRACTING',
              label: translate('knowledge.documentStatus.EXTRACTING'),
            },
            {
              value: 'EMBEDDING',
              label: translate('knowledge.documentStatus.EMBEDDING'),
            },
            {
              value: 'READY',
              label: translate('knowledge.documentStatus.READY'),
            },
            {
              value: 'FAILED',
              label: translate('knowledge.documentStatus.FAILED'),
            },
          ]}
          aria-label={translate('knowledge.filter.statusLabel')}
        />
        {canManageDocuments && (
          <LiksMenu
            className={[
              'knowledge-database__upload-menu',
              isUploading && 'knowledge-database__upload-menu--uploading',
            ]
              .filter(Boolean)
              .join(' ')}
            trigger={
              isUploading && uploadProgress ? (
                <>
                  <Loader2
                    className="knowledge-database__upload-spinner"
                    aria-hidden
                  />
                  <span className="knowledge-database__upload-menu-label">
                    {uploadProgress.current}/{uploadProgress.total}
                  </span>
                </>
              ) : (
                <>
                  <Plus aria-hidden />
                  <span className="knowledge-database__upload-menu-label">
                    {translate('knowledge.uploadDocument')}
                  </span>
                </>
              )
            }
            items={uploadMenuItems}
            placement="down"
            portalPanel
            matchTriggerWidth
            isOpen={isUploadMenuOpen}
            onOpenChange={(open) => {
              if (!isUploading) {
                setIsUploadMenuOpen(open);
              }
            }}
            onTriggerClick={() => {
              if (isUploading) {
                return;
              }

              setIsUploadMenuOpen((current) => !current);
            }}
            aria-label={translate('knowledge.uploadDocument')}
            triggerClassName="knowledge-database__upload-menu-trigger"
            panelClassName="knowledge-database__upload-menu-panel"
            itemClassName="knowledge-database__upload-menu-item"
          />
        )}
        <LiksRefreshButton
          className="knowledge-database__refresh-button"
          onRefresh={onManualRefresh}
        />
      </div>
      {canManageDocuments && (
        <div
          className={[
            'knowledge-database__upload-drop-zone',
            isUploadDragOver && 'knowledge-database__upload-drop-zone--active',
            isUploading && 'knowledge-database__upload-drop-zone--disabled',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={translate('knowledge.uploadDropZoneLabel')}
          aria-hidden={isUploading}
        >
          <Upload
            className="knowledge-database__upload-drop-zone-icon"
            aria-hidden
          />
          <p className="knowledge-database__upload-drop-zone-title">
            {isUploadDragOver
              ? translate('knowledge.uploadDropZoneActive')
              : translate('knowledge.uploadDropZoneTitle')}
          </p>
        </div>
      )}
    </>
  );
}
